import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { usePPCAuth, PPC_API_URL } from './PPCContext';
import RomeoReportDashboard from './RomeoReportDashboard';

// ─────────────────────────────────────────────────────────────────────────────
// CSS — exact match with finalised RomeoDashboard.html
// ─────────────────────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
:root{--red:#d32f2f;--blue:#1565c0;--blue-dk:#0d47a1;--grad:linear-gradient(135deg,#d32f2f 0%,#1565c0 100%);--green:#059669;--amber:#d97706;--bg:#f0f2f5;--surface:#fff;--border:#e2e6ea;--text:#1a1d23;--muted:#6b7280;--tray-bg:#f3e5f5;--tray-dk:#7b1fa2;--qty-bg:#dbeafe;--qty-dk:#1565c0;}
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:'DM Sans',sans-serif;background:var(--bg);color:var(--text);font-size:13px;min-height:100vh}
.r-header{background:var(--grad);padding:11px 20px;display:flex;justify-content:space-between;align-items:center;position:sticky;top:0;z-index:50;box-shadow:0 2px 14px rgba(211,47,47,.28)}
.r-header-left,.r-header-right{display:flex;align-items:center;gap:8px}
.r-back-btn,.r-hdr-btn{background:rgba(255,255,255,.16);border:1px solid rgba(255,255,255,.28);color:#fff;padding:6px 12px;border-radius:6px;cursor:pointer;font-size:12px;font-family:inherit;font-weight:500;transition:background .18s}
.r-back-btn:hover,.r-hdr-btn:hover{background:rgba(255,255,255,.28)}
.r-title{color:#fff;font-size:15px;font-weight:600;letter-spacing:.5px}
.r-role-badge{background:rgba(255,255,255,.2);color:#fff;font-size:10px;padding:3px 9px;border-radius:20px;font-weight:500}
input[type="date"].r-date-picker{background:rgba(255,255,255,.95);border:none;border-radius:6px;padding:6px 10px;font-size:12px;font-family:inherit;color:var(--text);font-weight:500;cursor:pointer}
.r-main{padding:14px 20px;display:grid;gap:12px}
.r-period-tabs{display:flex;background:var(--surface);border:1px solid var(--border);border-radius:10px;overflow:hidden;width:fit-content}
.r-period-tab{padding:8px 22px;font-size:12px;font-weight:500;font-family:inherit;border:none;background:transparent;color:var(--muted);cursor:pointer;transition:all .18s;border-right:1px solid var(--border)}
.r-period-tab:last-child{border-right:none}
.r-period-tab.active{background:var(--grad);color:#fff;font-weight:600}
.r-date-range{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
.r-date-range label{font-size:11px;color:var(--muted);font-weight:500}
.r-date-range input{padding:7px 10px;border:1px solid var(--border);border-radius:7px;font-family:inherit;font-size:12px;background:var(--surface);color:var(--text);outline:none}
.r-line-tab-bar{display:flex;background:var(--surface);border:1px solid var(--border);border-radius:10px;overflow:hidden;width:fit-content}
.r-line-tab{padding:9px 22px;font-size:13px;font-weight:600;font-family:inherit;border:none;background:transparent;color:var(--muted);cursor:pointer;transition:all .2s;border-right:1px solid var(--border);display:flex;align-items:center;gap:6px;letter-spacing:.3px}
.r-line-tab:last-child{border-right:none}
.r-line-tab.active{background:linear-gradient(135deg,#7b1fa2 0%,#1565c0 100%);color:#fff}
.r-line-dot{width:8px;height:8px;border-radius:50%;background:rgba(255,255,255,.5);flex-shrink:0}
.r-line-tab:not(.active) .r-line-dot{background:var(--muted)}
.r-stats-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:10px}
.r-stat-card{background:var(--surface);border:1px solid var(--border);border-radius:10px;padding:11px 14px;border-left:4px solid var(--blue);display:flex;align-items:center;gap:9px}
.r-stat-card.green{border-left-color:var(--green)}.r-stat-card.amber{border-left-color:var(--amber)}.r-stat-card.tray{border-left-color:var(--tray-dk)}
.r-stat-icon{font-size:19px}
.r-stat-label{font-size:10px;color:var(--muted);margin-bottom:3px}
.r-stat-value{font-size:14px;font-weight:600;font-family:'DM Mono',monospace}
.r-stat-value.green{color:var(--green)}.r-stat-value.amber{color:var(--amber)}.r-stat-value.blue{color:var(--blue)}.r-stat-value.tray{color:var(--tray-dk)}
.r-card{background:var(--surface);border:1px solid var(--border);border-radius:12px;overflow:hidden}
.r-card-header{padding:11px 16px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid var(--border);background:#fafbfc;gap:10px;flex-wrap:wrap}
.r-card-title{font-size:13px;font-weight:600;color:var(--text)}
.r-card-actions{display:flex;gap:7px;align-items:center}
.r-btn-add-global{padding:6px 14px;border-radius:7px;border:none;background:var(--green);color:#fff;font-family:inherit;font-size:12px;font-weight:700;cursor:pointer;transition:opacity .18s}
.r-btn-add-global:hover{opacity:.86}
.r-export-btn{padding:6px 12px;border-radius:6px;border:none;background:var(--grad);color:#fff;font-family:inherit;font-size:11px;font-weight:600;cursor:pointer}
.r-table-wrap{overflow-x:auto}
table.r-tbl{width:100%;border-collapse:collapse;font-size:11px;min-width:980px}
table.r-tbl thead th{padding:7px 8px;font-weight:600;font-size:10px;text-align:center;border:1px solid var(--border);white-space:nowrap;color:var(--text)}
.r-th-c{background:#f8f9fa;text-align:left!important;vertical-align:middle;padding-left:12px!important}
.r-th-dt{background:#f8f9fa;vertical-align:middle;min-width:85px}
.r-th-a{background:rgba(21,101,192,.08);color:var(--blue)}.r-th-s{background:rgba(217,119,6,.1);color:var(--amber)}
.r-th-d{background:rgba(211,47,47,.09);color:var(--red)}
.r-th-n{background:rgba(21,101,192,.09);color:var(--blue)}
.r-th-tray{background:var(--tray-bg);color:var(--tray-dk);vertical-align:middle}
.r-th-qty{background:var(--qty-bg);color:var(--qty-dk);vertical-align:middle}
.r-th-act{background:#f8f9fa;vertical-align:middle}
.r-th-sub{font-weight:500;font-size:10px;color:var(--muted)}
.r-tab-nav{display:flex;background:var(--surface);border-bottom:2px solid var(--border);padding:0 20px;position:sticky;top:52px;z-index:40}
.r-tab-item{display:flex;align-items:center;gap:7px;padding:11px 20px;font-size:13px;font-weight:500;font-family:inherit;border:none;background:transparent;color:var(--muted);cursor:pointer;border-bottom:3px solid transparent;margin-bottom:-2px;transition:all .18s;letter-spacing:.2px}
.r-tab-item.active{color:var(--blue);border-bottom-color:var(--blue);font-weight:600}
.r-tab-item:hover:not(.active){color:var(--text);background:#f8f9fa}
.r-tab-item-dot{width:8px;height:8px;border-radius:50%;background:currentColor;flex-shrink:0;opacity:.5}
.r-tab-item.active .r-tab-item-dot{opacity:1}
table.r-tbl tbody tr{transition:background .14s}
table.r-tbl tbody tr:hover{background:#f5f7ff}
table.r-tbl td{padding:7px 8px;border-bottom:1px solid var(--border);text-align:center;color:var(--text);vertical-align:middle}
table.r-tbl td:first-child{text-align:left;font-size:10px;color:var(--muted);font-family:'DM Mono',monospace;padding-left:10px}
table.r-tbl td.r-td-name{text-align:left;font-weight:600;font-size:11px;padding-left:12px;color:var(--text)}
.r-td-num{font-family:'DM Mono',monospace}
.r-td-grn{color:var(--green);font-family:'DM Mono',monospace}
.r-td-tray{background:var(--tray-bg);color:var(--tray-dk);font-weight:700;font-family:'DM Mono',monospace}
.r-td-qty{background:var(--qty-bg);color:var(--qty-dk);font-weight:700;font-family:'DM Mono',monospace}
.r-td-nil{color:#c9cdd4;font-size:12px}
table.r-tbl tfoot td{background:#f0f2f5;border-top:2px solid var(--border);font-weight:700;font-size:11px;text-align:center}
table.r-tbl tfoot td:first-child{text-align:left;padding-left:10px}
table.r-tbl tfoot td:nth-child(2){text-align:left;font-size:11px;font-weight:700;padding-left:12px}
table.r-tbl tfoot .r-td-tray{background:#e1bee7;color:#6a1b9a}
table.r-tbl tfoot .r-td-qty{background:#c7dbf9;color:var(--blue-dk)}
.r-act-cell{display:flex;gap:4px;justify-content:center;flex-wrap:wrap}
.r-btn-add{padding:3px 8px;border:none;border-radius:4px;background:var(--green);color:#fff;font-size:10px;font-weight:700;cursor:pointer;font-family:inherit}
.r-btn-edit{padding:3px 8px;border:none;border-radius:4px;background:var(--blue);color:#fff;font-size:10px;font-weight:700;cursor:pointer;font-family:inherit}
.r-btn-del{padding:3px 8px;border:none;border-radius:4px;background:var(--red);color:#fff;font-size:10px;font-weight:700;cursor:pointer;font-family:inherit}
.r-overlay{position:fixed;inset:0;background:rgba(0,0,0,.48);display:flex;align-items:center;justify-content:center;z-index:200;padding:14px;backdrop-filter:blur(2px)}
.r-modal{background:var(--surface);border-radius:16px;width:100%;max-height:95vh;overflow:hidden;display:flex;flex-direction:column;animation:rSlideUp .22s ease}
@keyframes rSlideUp{from{transform:translateY(22px);opacity:0}to{transform:translateY(0);opacity:1}}
.r-modal-hdr{background:var(--grad);padding:14px 18px;display:flex;justify-content:space-between;align-items:flex-start;flex-shrink:0;position:sticky;top:0}
.r-modal-title{color:#fff;font-size:15px;font-weight:600}
.r-modal-sub{color:rgba(255,255,255,.75);font-size:11px;margin-top:3px}
.r-modal-close{width:27px;height:27px;border-radius:50%;border:none;background:rgba(255,255,255,.2);color:#fff;font-size:16px;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.r-modal-scroll{padding:18px;overflow-y:auto;flex:1;display:grid;gap:13px}
.r-modal-foot{padding:12px 18px;border-top:1px solid var(--border);display:flex;gap:9px;background:#fafbfc;flex-shrink:0}
.r-f-sec{border-radius:10px;padding:13px}
.r-f-sec.info-s{background:#f0f4ff;border:1px solid #c7d6f7}
.r-f-sec.day-s{background:#fff8f8;border:1px solid #fca5a5}
.r-f-sec.night-s{background:#eff6ff;border:1px solid #93c5fd}
.r-f-sec.tray-s{background:var(--tray-bg);border:1px solid #ce93d8}
.r-f-sec.qty-s{background:var(--qty-bg);border:1px solid #93c5fd}
.r-f-sec.neut-s{background:#f8f9fa;border:1px solid var(--border)}
.r-f-lbl{font-size:11px;font-weight:700;margin-bottom:10px;letter-spacing:.3px}
.r-f-lbl.info{color:#1565c0}.r-f-lbl.day{color:var(--red)}.r-f-lbl.night{color:var(--blue)}.r-f-lbl.tray{color:var(--tray-dk)}.r-f-lbl.qty{color:var(--qty-dk)}
.r-fg2{display:grid;grid-template-columns:1fr 1fr;gap:11px}
.r-fg3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:11px}
.r-fg4{display:grid;grid-template-columns:repeat(4,1fr);gap:11px}
.r-ff label{display:block;font-size:10px;font-weight:700;color:var(--muted);margin-bottom:4px;text-transform:uppercase;letter-spacing:.4px}
.r-ff input,.r-ff select{width:100%;padding:9px 11px;border-radius:7px;border:1.5px solid var(--border);font-family:'DM Mono',monospace;font-size:12px;background:#fff;color:var(--text);outline:none;transition:border-color .18s,box-shadow .18s;box-sizing:border-box}
.r-ff select{font-family:'DM Sans',sans-serif}
.r-ff input:focus,.r-ff select:focus{border-color:var(--blue);box-shadow:0 0 0 3px rgba(21,101,192,.1)}
.r-ff input.i-day{border-color:#fca5a5}.r-ff input.i-night{border-color:#93c5fd}.r-ff input.i-tray{border-color:#ce93d8}
.r-ff input.ro{background:#f0fdf4;border-color:#86efac;color:var(--green);font-weight:600}
.r-ff input.ro-box{background:var(--tray-bg);border-color:#ce93d8;color:var(--tray-dk);font-weight:600}
.r-line-sel{display:flex;gap:10px}
.r-line-sel-btn{flex:1;padding:10px;border-radius:9px;border:2px solid var(--border);background:#fff;cursor:pointer;font-family:inherit;font-size:13px;font-weight:600;transition:all .2s;display:flex;align-items:center;justify-content:center;gap:7px;color:var(--muted)}
.r-line-sel-btn.active{border-color:#7b1fa2;background:linear-gradient(135deg,rgba(123,31,162,.1),rgba(21,101,192,.1));color:#7b1fa2}
.r-shift-btns{display:flex;gap:8px}
.r-shift-btn{flex:1;padding:9px;border-radius:7px;border:2px solid var(--border);background:#fff;color:var(--muted);font-family:inherit;font-size:12px;font-weight:500;cursor:pointer;transition:all .18s}
.r-shift-btn.active{border-color:var(--blue);background:#dbeafe;color:var(--blue);font-weight:700}
.r-hrs-btns{display:flex;gap:8px}
.r-hrs-btn{flex:1;padding:9px;border-radius:7px;border:2px solid var(--border);background:#fff;color:var(--muted);font-family:inherit;font-size:13px;font-weight:500;cursor:pointer;transition:all .18s}
.r-hrs-btn.active{border-color:var(--tray-dk);background:var(--tray-bg);color:var(--tray-dk);font-weight:700}
.r-btn-cancel{flex:1;padding:10px;border-radius:8px;border:1.5px solid var(--border);background:#fff;font-family:inherit;font-size:13px;font-weight:600;cursor:pointer}
.r-btn-save{flex:2;padding:10px;border-radius:8px;border:none;background:var(--grad);color:#fff;font-family:inherit;font-size:13px;font-weight:700;cursor:pointer}
.r-btn-save:disabled{opacity:.5;cursor:not-allowed}
.r-btn-del-ok{flex:2;padding:10px;border-radius:8px;border:none;background:linear-gradient(135deg,#dc2626,#ef4444);color:#fff;font-family:inherit;font-size:13px;font-weight:700;cursor:pointer}
.r-del-body{padding:30px 24px;text-align:center}
.r-del-icon{font-size:44px;margin-bottom:14px}
.r-del-ttl{font-size:17px;font-weight:700;color:var(--red);margin-bottom:5px}
.r-del-sub{font-size:12px;color:var(--muted);margin-bottom:4px}
.r-del-nm{font-size:14px;font-weight:700;margin-bottom:20px}
.r-del-btns{display:flex;gap:10px}
.r-toast{position:fixed;bottom:22px;right:22px;padding:11px 18px;border-radius:9px;color:#fff;font-size:13px;font-weight:500;z-index:999;box-shadow:0 4px 18px rgba(0,0,0,.28);animation:rSlideUp .22s ease}
.r-toast.success{background:var(--green)}.r-toast.error{background:var(--red)}
.r-line-label{display:flex;align-items:center;gap:7px;margin-bottom:6px}
.r-line-label span{font-size:11px;font-weight:700;letter-spacing:.4px;text-transform:uppercase}
.r-summary-tiles{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:10px}
.r-tile{background:var(--surface);border:1px solid var(--border);border-radius:12px;padding:16px;position:relative;overflow:hidden}
.r-tile::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:var(--grad)}
.r-tile.blue::before{background:var(--blue)}.r-tile.green::before{background:var(--green)}.r-tile.amber::before{background:var(--amber)}
.r-tile-label{font-size:10px;color:var(--muted);font-weight:600;text-transform:uppercase;letter-spacing:.4px;margin-bottom:6px}
.r-tile-value{font-size:20px;font-weight:700;font-family:'DM Mono',monospace}
.r-tile-sub{font-size:11px;color:var(--muted);margin-top:4px}
.r-breakdown-card{background:var(--surface);border:1px solid var(--border);border-radius:12px;overflow:hidden}
.r-breakdown-hdr{padding:11px 16px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid var(--border);background:#fafbfc}
.r-breakdown-title{font-size:13px;font-weight:600}
@media(max-width:900px){.r-stats-grid{grid-template-columns:repeat(3,1fr)}}
@media(max-width:600px){.r-stats-grid{grid-template-columns:1fr 1fr}.r-main{padding:10px}}
.r-qty-tbl{width:100%;border-collapse:collapse;font-size:11px}
.r-qty-tbl th{padding:7px 9px;background:#dbeafe;color:#1565c0;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.3px;border:1px solid #93c5fd;text-align:center}
.r-qty-tbl td{padding:5px 6px;border:1px solid #bdd7f7;vertical-align:middle;background:#fff}
.r-qty-tbl tr:hover td{background:#eff6ff}
.r-qty-tbl input{width:100%;padding:6px 8px;border:1.5px solid #c7dbf9;border-radius:5px;font-family:'DM Mono',monospace;font-size:11px;background:transparent;outline:none;box-sizing:border-box;transition:border-color .15s}
.r-qty-tbl input:focus{border-color:#1565c0;background:#fff;box-shadow:0 0 0 2px rgba(21,101,192,.1)}
.r-qty-add-btn{display:inline-flex;align-items:center;gap:5px;padding:6px 14px;border:none;border-radius:6px;background:linear-gradient(135deg,#1565c0,#1976d2);color:#fff;font-size:11px;font-weight:700;cursor:pointer;font-family:inherit;transition:opacity .15s;letter-spacing:.2px}
.r-qty-add-btn:hover{opacity:.86}
.r-qty-del-btn{width:22px;height:22px;border:none;border-radius:4px;background:rgba(211,47,47,.1);color:var(--red);font-size:14px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background .15s;font-weight:700;line-height:1}
.r-qty-del-btn:hover{background:rgba(211,47,47,.22)}
.r-qty-total{display:flex;align-items:center;justify-content:flex-end;gap:8px;padding:10px 4px 0;border-top:1.5px dashed #93c5fd;margin-top:8px}
.r-gender-btns{display:flex;gap:8px}
.r-gender-btn{flex:1;padding:10px 12px;border-radius:7px;border:2px solid var(--border);background:var(--surface);color:var(--muted);font-family:inherit;font-size:12px;font-weight:500;cursor:pointer;transition:all .18s;display:flex;align-items:center;justify-content:center;gap:6px}
.r-gender-btn.male-active{border-color:#1565c0;background:#dbeafe;color:#1565c0;font-weight:700}
.r-gender-btn.female-active{border-color:#d97706;background:#fff8ee;color:#d97706;font-weight:700}
.r-gender-count{display:flex;align-items:center;gap:10px;margin-top:10px}
.r-gender-count-box{flex:1;display:flex;flex-direction:column;gap:4px}
.r-gender-count-box label{font-size:10px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.4px}
.r-gender-count-box input{width:100%;padding:8px 10px;border-radius:7px;border:1.5px solid var(--border);font-family:'DM Mono',monospace;font-size:13px;background:var(--surface);color:var(--text);outline:none;transition:border-color .15s}
.r-gender-count-box input.male-inp{border-color:#93c5fd;background:#eff6ff;color:#1565c0;font-weight:600}
.r-gender-count-box input.female-inp{border-color:#fed7aa;background:#fff8ee;color:#d97706;font-weight:600}
.r-gender-count-box input.total-inp{border-color:#86efac;background:#f0fdf4;color:#059669;font-weight:700}
`;

// ── Helpers ───────────────────────────────────────────────────────────────────
const TODAY    = new Date().toISOString().slice(0, 10);
const fmt      = (v, d = 2) => parseFloat(v || 0).toFixed(d);
const fmtDate  = (d) => { if (!d) return '—'; const dt = new Date(d + 'T00:00:00'); return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); };

const LINES = ['ROMEO 1', 'ROMEO 2'];

const emptyQtyRow = () => ({ id: Date.now() + Math.random(), lotNumber: '', honCount: '', honQty: '', hours: '' });

const emptyAdd = () => ({
  line: 'ROMEO 1', contractor: '', date: TODAY, shift: 'DAY',
  gender: 'MALE',          // MALE | FEMALE
  assocMale: 0, suppMale: 0,
  trays: '', vehicles: '', shiftHours: 8,
  kgPerTray: 21,
  quantityRows: [emptyQtyRow()],
  remarks: ''
});

// ─────────────────────────────────────────────────────────────────────────────
const RomeoDashboard = () => {
  const { user } = usePPCAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const isAdmin   = user?.role === 'admin';
  const cssRef    = useRef(false);

  // Inject CSS once
  useEffect(() => {
    if (cssRef.current) return;
    const el = document.createElement('style');
    el.textContent = CSS;
    document.head.appendChild(el);
    cssRef.current = true;
  }, []);

  // ── State ─────────────────────────────────────────────────────────────────
  const [period,       setPeriod]       = useState('daily');
  const [selectedDate, setSelectedDate] = useState(TODAY);
  const [weekFrom,     setWeekFrom]     = useState('');
  const [weekTo,       setWeekTo]       = useState('');
  const [monthPicker,  setMonthPicker]  = useState('');
  const [activeLine,   setActiveLine]   = useState('ROMEO 1');
  const [summary,      setSummary]      = useState(null);
  const [canEdit,      setCanEdit]      = useState(false);
  const [toast,        setToast]        = useState(null);
  const [submitting,   setSubmitting]   = useState(false);
  const [contractors,  setContractors]  = useState([]);
  const [reportData,   setReportData]   = useState(null);
  const [reportLoading,setReportLoading]= useState(false);

  // Add modal
  const [showAdd,   setShowAdd]   = useState(false);
  const [addForm,   setAddForm]   = useState(emptyAdd());

  // Tab navigation
  const [activeTab, setActiveTab] = useState('dashboard');

  // Edit modal
  const [showEdit,  setShowEdit]  = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [editReason,setEditReason]= useState('');

  // Delete modal
  const [showDel,   setShowDel]   = useState(false);
  const [delRow,    setDelRow]    = useState(null);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  };

  const pw = (e) => e.target.blur();

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchSummary = useCallback(async () => {
    if (!selectedDate) return;
    try {
      const res = await axios.get(
        `${PPC_API_URL}/romeo.php?action=summary&date=${selectedDate}`,
        auth()
      );
      if (res.data && res.data.success) {
        setSummary(res.data);
        setCanEdit(res.data.canEdit ?? false);
      } else {
        console.warn('Romeo summary returned non-success:', res.data);
        setSummary(prev => prev); // keep previous data visible
      }
    } catch (e) {
      console.error('Romeo fetchSummary error:', e.response?.data || e.message);
    }
  }, [selectedDate]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  // Fetch contractors for ROMEO module from master list
  useEffect(() => {
    const fetchContractors = async () => {
      const FALLBACK = ['DBS','KRISHNA','PRABHAS','SAMUDRA','SHIVA','SHUKOOR ISM','SONA CIST','VENKATESWARA'];
      try {
        const res = await axios.get(
          `${PPC_API_URL}/contractors.php?action=by-module&module=ROMEO`,
          auth()
        );
        if (res.data.success && res.data.contractors.length > 0) {
          setContractors(res.data.contractors.map(c => c.name));
        } else {
          // Module not yet assigned in contractor master — use fallback
          setContractors(FALLBACK);
        }
      } catch {
        setContractors(FALLBACK);
      }
    };
    fetchContractors();
  }, []);

  // ── Fetch weekly / monthly report ─────────────────────────────────────────
  const fetchReport = useCallback(async (from, to) => {
    if (!from || !to) return;
    console.log('Romeo fetchReport — from:', from, 'to:', to);
    setReportLoading(true);
    setReportData(null);
    try {
      const res = await axios.get(
        `${PPC_API_URL}/romeo.php?action=report&from=${from}&to=${to}`,
        auth()
      );
      console.log('Romeo fetchReport — response:', res.data);
      if (res.data.success) {
        setReportData(res.data);
      } else {
        console.error('Romeo fetchReport returned non-success:', res.data);
      }
    } catch (e) {
      console.error('Romeo fetchReport error:', e.response?.data || e.message);
    } finally {
      setReportLoading(false);
    }
  }, []);

  // FIX B4a: auto-fill current week when switching to weekly (Mon–Sun of current week)
  const getCurrentWeek = () => {
    const today = new Date();
    const day   = today.getDay(); // 0=Sun,1=Mon,...
    const mon   = new Date(today); mon.setDate(today.getDate() - ((day + 6) % 7));
    const sun   = new Date(mon);   sun.setDate(mon.getDate() + 6);
    const fmt2  = (d) => d.toISOString().slice(0, 10);
    return { from: fmt2(mon), to: fmt2(sun) };
  };

  // FIX B4b: when period changes, clear old report and auto-fetch if dates already set
  useEffect(() => {
    setReportData(null);
    if (period === 'weekly') {
      // Auto-fill current week if not already set
      if (!weekFrom || !weekTo) {
        const { from, to } = getCurrentWeek();
        setWeekFrom(from);
        setWeekTo(to);
        // fetchReport will be triggered by the weekFrom/weekTo useEffect below
      } else {
        // Dates already set — re-fetch immediately on tab switch
        fetchReport(weekFrom, weekTo);
      }
    } else if (period === 'monthly') {
      // Auto-fill current month if not already set
      if (!monthPicker) {
        const today = new Date();
        const ym = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
        setMonthPicker(ym);
        // fetchReport will be triggered by the monthPicker useEffect below
      } else {
        // Month already set — re-fetch immediately
        const parts    = monthPicker.split('-');
        const yr       = parseInt(parts[0]);
        const mo       = parseInt(parts[1]);
        const from     = `${yr}-${String(mo).padStart(2, '0')}-01`;
        const lastDay  = new Date(yr, mo, 0).getDate();
        const to       = `${yr}-${String(mo).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        fetchReport(from, to);
      }
    }
  }, [period]); // eslint-disable-line

  // FIX B4c: fetch when weekFrom OR weekTo changes (both must be valid)
  useEffect(() => {
    if (period === 'weekly' && weekFrom && weekTo && weekFrom <= weekTo) {
      console.log('Romeo weekly useEffect — fetching', weekFrom, weekTo);
      fetchReport(weekFrom, weekTo);
    }
  }, [weekFrom, weekTo, fetchReport]); // eslint-disable-line

  // FIX B4d: fetch when monthPicker changes (auto-triggers, no "Generate" button needed)
  useEffect(() => {
    if (period === 'monthly' && monthPicker) {
      const parts   = monthPicker.split('-');
      const yr      = parseInt(parts[0]);
      const mo      = parseInt(parts[1]);
      const from    = `${yr}-${String(mo).padStart(2, '0')}-01`;
      const lastDay = new Date(yr, mo, 0).getDate();
      const to      = `${yr}-${String(mo).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      fetchReport(from, to);
    }
  }, [monthPicker, fetchReport]); // eslint-disable-line

  // Auto-open Add modal when navigated from PPC home panel with ?add=1
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('add') === '1') {
      const contractor = decodeURIComponent(params.get('contractor') || '');
      const shift      = params.get('shift') || 'DAY';
      // Find matching line, default to active
      const line = ['ROMEO 1','ROMEO 2'].includes(contractor) ? contractor : activeLine;
      setAddForm({ ...emptyAdd(), line, date: selectedDate, shift });
      setActiveLine(line);
      setShowAdd(true);
      // Clean URL without reload
      window.history.replaceState({}, '', location.pathname);
    }
    if (params.get('edit') === '1') {
      // Just ensure correct date is shown; user can click EDIT on the row
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location.search]);

  // ── Build table rows for active line ─────────────────────────────────────
  // Only show contractors that have actual entries for the active line
  const rowsForLine = (line) => {
    return (summary?.contractorSummary || []).filter(r => r.line === line);
  };

  // ── Stat totals for a line ───────────────────────────────────────────────
  const statsForLine = (line) => {
    // Stats from overall summary (all lines combined if no filter)
    // For per-line stats we filter contractorSummary by line
    const rows = (summary?.contractorSummary || []).filter(r => r.line === line);
    return {
      trays:    rows.reduce((s, r) => s + parseInt(r.total_trays    || 0), 0),
      qty:      rows.reduce((s, r) => s + parseFloat(r.total_quantity || 0), 0),
      assoc:    rows.reduce((s, r) => s + parseInt(r.total_associates|| 0), 0),
      dayTrays: rows.reduce((s, r) => s + parseInt(r.day_trays || 0), 0),
    };
  };

  // ── Add form helpers ─────────────────────────────────────────────────────
  const setAF = (k, v) => setAddForm(p => ({ ...p, [k]: v }));

  const calcAddAuto = (form) => {
    const trays   = parseFloat(form.trays)      || 0;
    const hrs     = parseFloat(form.shiftHours) || 8;
    const kgTray  = parseFloat(form.kgPerTray)  || 21;
    const rowQty  = (form.quantityRows || []).reduce((s, r) => s + (parseFloat(r.honQty) || 0), 0);
    const autoQty = rowQty > 0 ? rowQty : trays * kgTray;
    return { traysPerHr: hrs > 0 ? (trays / hrs).toFixed(2) : '0.00', autoQty: autoQty > 0 ? autoQty.toFixed(2) : '' };
  };

  // Helper to update a single quantity row field
  const updateQtyRow = (idx, field, value) => {
    const rows = [...addForm.quantityRows];
    rows[idx] = { ...rows[idx], [field]: value };
    setAF('quantityRows', rows);
  };

  const openAddGlobal = () => { setAddForm({ ...emptyAdd(), line: activeLine, date: selectedDate }); setShowAdd(true); };
  const openAddRow    = (line, shift) => { setAddForm({ ...emptyAdd(), line, date: selectedDate, shift }); setShowAdd(true); };

  const handleSaveAdd = async () => {
    // ── Validation ────────────────────────────────────────────────────────
    if (!addForm.line)       { showToast('Select a line', 'error');       return; }
    if (!addForm.contractor) { showToast('Select a contractor', 'error'); return; }
    if (!addForm.date)       { showToast('Select a date', 'error');       return; }
    if (!addForm.shift)      { showToast('Select a shift', 'error');      return; }

    const trays    = parseInt(addForm.trays)     || 0;
    const male     = parseInt(addForm.assocMale) || 0;
    const female   = parseInt(addForm.suppMale)  || 0;
    const totalAss = male + female;

    if (trays <= 0 && totalAss <= 0) {
      showToast('Enter at least trays or associates count', 'error'); return;
    }

    // FIX F1: Validate ALL quantity rows (not just rows with lotNumber)
    const allRows   = addForm.quantityRows || [];
    const validRows = allRows.filter(r => String(r.lotNumber || '').trim() !== '');

    for (const r of validRows) {
      if (r.honCount !== '' && r.honCount !== undefined &&
          (isNaN(parseFloat(r.honCount)) || parseFloat(r.honCount) < 0)) {
        showToast('H.ON Count must be a valid non-negative number', 'error'); return;
      }
      if (r.honQty !== '' && r.honQty !== undefined &&
          (isNaN(parseFloat(r.honQty)) || parseFloat(r.honQty) < 0)) {
        showToast('H.ON Qty must be a valid non-negative number', 'error'); return;
      }
      if (r.hours !== '' && r.hours !== undefined &&
          (isNaN(parseFloat(r.hours)) || parseFloat(r.hours) < 0)) {
        showToast('Hours must be a valid non-negative number', 'error'); return;
      }
    }

    const kgTray  = parseFloat(addForm.kgPerTray)  || 21;
    const hrs     = parseFloat(addForm.shiftHours) || 8;
    const rowQty  = validRows.reduce((s, r) => s + (parseFloat(r.honQty) || 0), 0);
    const autoQty = rowQty > 0 ? rowQty : trays * kgTray;

    // FIX F2: Ensure quantity_rows uses correct camelCase keys matching PHP handler
    // PHP accepts both camelCase and snake_case — send camelCase (frontend native)
    const quantityRows = validRows.map(r => ({
      lotNumber: String(r.lotNumber || '').trim(),
      honCount:  parseInt(r.honCount)  || 0,
      honQty:    parseFloat(r.honQty)  || 0,
      hours:     parseFloat(r.hours)   || 0,
    }));

    // ── Payload ──────────────────────────────────────────────────────────
    const payload = {
      entry_date:        addForm.date,
      line:              addForm.line,
      contractor:        addForm.contractor,
      shift:             addForm.shift,
      gender_type:       addForm.gender || 'MALE',
      male_associates:   male,
      supporting_male:   female,
      total_associates:  totalAss,
      total_no_trays:    trays,
      total_no_vehicles: parseInt(addForm.vehicles) || 0,
      shift_hours:       hrs,
      kg_per_tray:       kgTray,
      trays_per_hour:    hrs > 0 ? parseFloat((trays / hrs).toFixed(4)) : 0,
      total_quantity:    parseFloat(autoQty.toFixed(4)) || 0,
      qty_per_tray:      trays > 0 ? parseFloat((autoQty / trays).toFixed(4)) : 0,
      quantity_rows:     quantityRows,    // FIX F2: always send array (even if empty)
      remarks:           addForm.remarks || '',
    };

    // FIX F3: Debug logging (visible in browser DevTools → Console tab)
    console.log('Romeo Save — Payload:', JSON.stringify(payload, null, 2));
    console.log('Romeo Save — quantity_rows count:', quantityRows.length, quantityRows);

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${PPC_API_URL}/romeo.php?action=entries`,
        payload,
        { headers: { ...auth().headers, 'Content-Type': 'application/json' } }
      );

      // FIX F3: Log full API response
      console.log('Romeo Save — API Response:', res.data);

      if (res.data && res.data.success) {
        const saved = res.data.updated ? '✅ Entry updated!' : '✅ Entry saved!';
        const rowInfo = res.data.rows_saved > 0 ? ` (${res.data.rows_saved} lot rows)` : '';
        showToast(saved + rowInfo);
        setShowAdd(false);
        setAddForm(emptyAdd());
        setTimeout(() => fetchSummary(), 300);
      } else {
        // FIX F4: Show actual server error — detail field gives column-level info
        const errMsg = res.data?.error
          || res.data?.message
          || 'Save failed';
        const detail = res.data?.detail
          ? ' — ' + JSON.stringify(res.data.detail)
          : (res.data?.hint ? ' — ' + res.data.hint : '');
        console.error('Romeo save failed:', res.data);
        showToast(errMsg + detail, 'error');
      }
    } catch (e) {
      // FIX F4: Full error dump — catches network errors AND non-JSON responses
      const isJsonError = e.message?.includes('JSON') || e.message?.includes('json');
      const serverMsg = e.response?.data?.error
        || e.response?.data?.message
        || (isJsonError ? 'Server returned non-JSON (PHP error) — check WAMP error log' : e.message)
        || 'Network error';
      console.error('Romeo save exception:', {
        status:   e.response?.status,
        data:     e.response?.data,
        message:  e.message,
        payload,
      });
      showToast(`Save error: ${serverMsg}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Edit helpers ─────────────────────────────────────────────────────────
  const openEdit = (row, shiftFocus) => {
    setEditEntry({
      contractor:    row.contractor,
      line:          row.line || activeLine,
      date:          selectedDate,
      dayEntryId:    row.day_entry_id   ? parseInt(row.day_entry_id)   : null,
      nightEntryId:  row.night_entry_id ? parseInt(row.night_entry_id) : null,
      dayTrays:      parseInt(row.day_trays   || 0),
      nightTrays:    parseInt(row.night_trays || 0),
      dayVehicles:   parseInt(row.day_vehicles   || 0),
      nightVehicles: parseInt(row.night_vehicles || 0),
      dayHrs:        parseInt(row.day_shift_hours   || 8),
      nightHrs:      parseInt(row.night_shift_hours || 8),
      dayKg:         parseFloat(row.avg_kg_per_tray || 21),
      nightKg:       parseFloat(row.avg_kg_per_tray || 21),
      dayQty:        parseFloat(row.day_qty   || 0),
      nightQty:      parseFloat(row.night_qty || 0),
      assocMale:     parseInt(row.male_associates   || 0),
      suppMale:      parseInt(row.supporting_male   || 0),
    });
    setEditReason('');
    setShowEdit(true);
  };
  const setEE = (k, v) => setEditEntry(p => ({ ...p, [k]: v }));

  const handleSaveEdit = async () => {
    if (!editEntry) return;
    try {
      if (isAdmin || canEdit) {
        const calls = [];
        if (editEntry.dayEntryId) calls.push(
          axios.put(`${PPC_API_URL}/romeo.php?action=edit-entry`, {
            entry_id: editEntry.dayEntryId,
            line:              editEntry.line,
            contractor:        editEntry.contractor,
            male_associates:   editEntry.assocMale,
            supporting_male:   editEntry.suppMale,
            total_no_trays:    editEntry.dayTrays,
            total_no_vehicles: editEntry.dayVehicles,
            shift_hours:       editEntry.dayHrs,
            kg_per_tray:       editEntry.dayKg,
            total_quantity:    editEntry.dayQty,
          }, auth())
        );
        if (editEntry.nightEntryId) calls.push(
          axios.put(`${PPC_API_URL}/romeo.php?action=edit-entry`, {
            entry_id: editEntry.nightEntryId,
            line:              editEntry.line,
            contractor:        editEntry.contractor,
            male_associates:   editEntry.assocMale,
            supporting_male:   editEntry.suppMale,
            total_no_trays:    editEntry.nightTrays,
            total_no_vehicles: editEntry.nightVehicles,
            shift_hours:       editEntry.nightHrs,
            kg_per_tray:       editEntry.nightKg,
            total_quantity:    editEntry.nightQty,
          }, auth())
        );
        await Promise.all(calls);
        showToast('Entry updated!');
        setShowEdit(false);
        fetchSummary();
      } else {
        const res = await axios.post(`${PPC_API_URL}/edit_requests.php?action=submit`, {
          dashboard: 'romeo', entry_id: editEntry.dayEntryId || editEntry.nightEntryId,
          entry_date: editEntry.date, contractor: editEntry.contractor,
          shift: 'ALL', request_type: 'edit', reason: editReason,
          old_values: { day_trays: editEntry.dayTrays, night_trays: editEntry.nightTrays },
          new_values: { day_trays: editEntry.dayTrays, night_trays: editEntry.nightTrays,
            day_qty: editEntry.dayQty, night_qty: editEntry.nightQty }
        }, auth());
        if (res.data.success) { showToast('Edit request submitted!'); setShowEdit(false); }
      }
    } catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
  };

  // ── Delete ───────────────────────────────────────────────────────────────
  const openDel  = (row) => { setDelRow(row); setShowDel(true); };
  const handleDel = async () => {
    if (!delRow) return;
    try {
      const ids = [delRow.day_entry_id, delRow.night_entry_id].filter(Boolean);
      await Promise.all(ids.map(id =>
        axios.delete(`${PPC_API_URL}/romeo.php?action=delete-entry&entry_id=${id}`, auth())
      ));
      showToast('Entry deleted!');
      setShowDel(false);
      fetchSummary();
    } catch { showToast('Delete failed', 'error'); }
  };

  // ── Export ───────────────────────────────────────────────────────────────
  const handleExport = () => {
    const rows = summary?.contractorSummary || [];
    if (!rows.length) { showToast('No data to export', 'error'); return; }

    const headers = [
      'Date', 'Line', 'Contractor', 'Shift',
      'Male Associates', 'Female Support', 'Total Associates',
      'Day Trays', 'Day Qty (KG)',
      'Night Trays', 'Night Qty (KG)',
      'Total Trays', 'Total Qty (KG)',
      'Avg KG/Tray', 'Trays/Hour'
    ];

    const data = rows.map(r => [
      selectedDate,
      r.line || '',
      r.contractor || '',
      r.shift || 'BOTH',
      parseInt(r.male_associates   || 0),
      parseInt(r.supporting_male   || 0),
      parseInt(r.total_associates  || 0) || (parseInt(r.male_associates || 0) + parseInt(r.supporting_male || 0)),
      parseInt(r.day_trays   || 0),
      parseFloat(r.day_qty   || 0).toFixed(2),
      parseInt(r.night_trays || 0),
      parseFloat(r.night_qty || 0).toFixed(2),
      parseInt(r.total_trays    || 0),
      parseFloat(r.total_quantity|| 0).toFixed(2),
      parseFloat(r.avg_kg_per_tray || r.kg_per_tray || 0).toFixed(2),
      parseFloat(r.trays_per_hour  || 0).toFixed(2),
    ]);

    // Totals row
    const totals = [
      'TOTAL', '', '', '',
      rows.reduce((s,r) => s + parseInt(r.male_associates   || 0), 0),
      rows.reduce((s,r) => s + parseInt(r.supporting_male   || 0), 0),
      rows.reduce((s,r) => s + (parseInt(r.total_associates || 0) || parseInt(r.male_associates || 0) + parseInt(r.supporting_male || 0)), 0),
      rows.reduce((s,r) => s + parseInt(r.day_trays   || 0), 0),
      rows.reduce((s,r) => s + parseFloat(r.day_qty   || 0), 0).toFixed(2),
      rows.reduce((s,r) => s + parseInt(r.night_trays || 0), 0),
      rows.reduce((s,r) => s + parseFloat(r.night_qty || 0), 0).toFixed(2),
      rows.reduce((s,r) => s + parseInt(r.total_trays    || 0), 0),
      rows.reduce((s,r) => s + parseFloat(r.total_quantity|| 0), 0).toFixed(2),
      '—', '—',
    ];

    const wb = XLSX.utils.book_new();
    const wsData = [
      ['ROMEO Dashboard Export'],
      ['Date:', selectedDate, 'Line Filter:', activeLine || 'All'],
      ['Generated:', new Date().toLocaleString('en-IN')],
      [],
      headers,
      ...data,
      [],
      totals,
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    // Column widths
    ws['!cols'] = [12,10,16,8,8,8,8,8,12,8,12,8,12,10,10].map(w => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws, 'Romeo Summary');
    XLSX.writeFile(wb, `ROMEO_${selectedDate}.xlsx`);
    showToast('✅ Exported!');
  };

  // ── Render helpers ───────────────────────────────────────────────────────
  const { traysPerHr, autoQty } = calcAddAuto(addForm);

  const renderLineStats = (line) => {
    const s    = statsForLine(line);
    const isL1 = line === 'ROMEO 1';
    const dotColor = isL1 ? 'linear-gradient(135deg,#7b1fa2,#1565c0)' : 'linear-gradient(135deg,#1565c0,#0d47a1)';
    const lblColor = isL1 ? '#7b1fa2' : '#1565c0';
    return (
      <div key={line}>
        <div className="r-line-label">
          <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: dotColor, display: 'inline-block' }} />
          <span style={{ color: lblColor }}>{line}</span>
        </div>
        <div className="r-stats-grid" style={{ marginBottom: '8px' }}>
          <div className="r-stat-card tray"><span className="r-stat-icon">🗂️</span><div><div className="r-stat-label">Total Trays</div><div className="r-stat-value tray">{s.trays}</div></div></div>
          <div className="r-stat-card green"><span className="r-stat-icon">⚖️</span><div><div className="r-stat-label">Total Quantity</div><div className="r-stat-value green">{fmt(s.qty)} KG</div></div></div>
          <div className="r-stat-card"><span className="r-stat-icon">👷</span><div><div className="r-stat-label">Male Associates</div><div className="r-stat-value blue">{s.assoc}</div></div></div>
          <div className="r-stat-card amber"><span className="r-stat-icon">☀️</span><div><div className="r-stat-label">Day Trays</div><div className="r-stat-value amber">{s.dayTrays}</div></div></div>
          <div className="r-stat-card"><span className="r-stat-icon">🌙</span><div><div className="r-stat-label">Night Trays</div><div className="r-stat-value blue">{s.trays - s.dayTrays}</div></div></div>
        </div>
      </div>
    );
  };

  const renderTable = (line) => {
    const rows = rowsForLine(line);

    const totTrays  = rows.reduce((s,r) => s + parseInt(r.total_trays    || 0), 0);
    const totQty    = rows.reduce((s,r) => s + parseFloat(r.total_quantity|| 0), 0);
    const totDayT   = rows.reduce((s,r) => s + parseInt(r.day_trays      || 0), 0);
    const totNightT = rows.reduce((s,r) => s + parseInt(r.night_trays    || 0), 0);
    const totDayQ   = rows.reduce((s,r) => s + parseFloat(r.day_qty      || 0), 0);
    const totNightQ = rows.reduce((s,r) => s + parseFloat(r.night_qty    || 0), 0);

    return (
      <div className="r-table-wrap">
        <table className="r-tbl">
          <thead>
            <tr>
              <th className="r-th-dt" rowSpan={2} style={{minWidth:'82px'}}>Date</th>
              <th className="r-th-c"  rowSpan={2} style={{minWidth:'100px'}}>Contractor</th>
              <th className="r-th-c"  rowSpan={2} style={{minWidth:'72px'}}>Line</th>
              <th className="r-th-a"  rowSpan={2} style={{minWidth:'90px',fontSize:'10px'}}>Male<br/>Associates</th>
              <th className="r-th-s"  rowSpan={2} style={{minWidth:'90px',fontSize:'10px'}}>Support<br/>Associates</th>
              <th className="r-th-d"  colSpan={2} style={{minWidth:'160px'}}>Day Shift</th>
              <th className="r-th-n"  colSpan={2} style={{minWidth:'160px'}}>Night Shift</th>
              <th className="r-th-tray" rowSpan={2} style={{minWidth:'70px'}}>Total<br/>Trays</th>
              <th className="r-th-qty"  rowSpan={2} style={{minWidth:'90px'}}>Total<br/>Qty (KG)</th>
              <th className="r-th-act"  rowSpan={2}>Actions</th>
            </tr>
            <tr>
              <th className="r-th-sub r-th-d" style={{minWidth:'80px'}}>Trays</th>
              <th className="r-th-sub r-th-d" style={{minWidth:'80px'}}>Qty (KG)</th>
              <th className="r-th-sub r-th-n" style={{minWidth:'80px'}}>Trays</th>
              <th className="r-th-sub r-th-n" style={{minWidth:'80px'}}>Qty (KG)</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                  No entries for {line} today — click ＋ Add Entry to get started
                </td>
              </tr>
            ) : rows.map((row, i) => {
              const hasDay   = !!row.day_entry_id;
              const hasNight = !!row.night_entry_id;
              const hasAny   = hasDay || hasNight;
              const hasBoth  = hasDay && hasNight;
              return (
                <tr key={i}>
                  <td>{fmtDate(selectedDate)}</td>
                  <td className="r-td-name">{row.contractor}</td>
                  <td className="r-td-name" style={{color:'#7b1fa2'}}>{row.line}</td>
                  <td className="r-td-num">{row.male_associates || 0}</td>
                  <td className="r-td-num" style={{color:'var(--amber)',fontWeight:600}}>
                    {row.supporting_male || 0}
                    {row.gender_type && row.gender_type !== 'MALE' &&
                      <span style={{fontSize:'9px',marginLeft:'4px',padding:'1px 5px',borderRadius:'8px',background:'rgba(217,119,6,.1)',color:'#d97706',fontWeight:600}}>
                        {row.gender_type === 'FEMALE' ? '👩F' : '👥'}
                      </span>
                    }
                  </td>
                  <td className="r-td-num">{hasDay   ? (row.day_trays   || 0) : <span className="r-td-nil">—</span>}</td>
                  <td className={hasDay   ? 'r-td-grn' : ''}>{hasDay   ? fmt(row.day_qty)   : <span className="r-td-nil">—</span>}</td>
                  <td className="r-td-num">{hasNight ? (row.night_trays || 0) : <span className="r-td-nil">—</span>}</td>
                  <td className={hasNight ? 'r-td-grn' : ''}>{hasNight ? fmt(row.night_qty) : <span className="r-td-nil">—</span>}</td>
                  <td className="r-td-tray">{row.total_trays || 0}</td>
                  <td className="r-td-qty">{fmt(row.total_quantity)}</td>
                  <td>
                    <div className="r-act-cell">
                      {!hasBoth && (
                        <button className="r-btn-add" onClick={() => openAddRow(line, hasDay ? 'NIGHT' : 'DAY', row.contractor)}>ADD</button>
                      )}
                      {hasAny && (
                        <button className="r-btn-edit" onClick={() => openEdit(row)}>EDIT</button>
                      )}
                      {hasAny && isAdmin && (
                        <button className="r-btn-del" onClick={() => openDel(row)}>DELETE</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td></td>
              <td style={{ textAlign:'left', paddingLeft:'12px', fontWeight:700 }}>TOTAL</td>
              <td></td>
              <td></td>
              <td></td>
              <td className="r-td-num">{totDayT}</td>
              <td className="r-td-grn">{fmt(totDayQ)}</td>
              <td className="r-td-num">{totNightT}</td>
              <td className="r-td-grn">{fmt(totNightQ)}</td>
              <td className="r-td-tray">{totTrays}</td>
              <td className="r-td-qty">{fmt(totQty)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  // ── Edit shift section helper ────────────────────────────────────────────
  const EditShiftSection = ({ label, prefix, cls, borderColor }) => {
    const trays = editEntry?.[`${prefix}Trays`]  || 0;
    const hrs   = editEntry?.[`${prefix}Hrs`]    || 8;
    const trHr  = hrs > 0 ? (trays / hrs).toFixed(2) : '0.00';
    const qty   = editEntry?.[`${prefix}Qty`]    || 0;
    const qTray = trays > 0 ? (qty / trays).toFixed(2) : '0.00';
    return (
      <div className={`r-f-sec ${cls}`}>
        <div className={`r-f-lbl ${prefix === 'day' ? 'day' : 'night'}`}>{label}</div>
        <div className="r-fg4">
          <div className="r-ff">
            <label>Total No of Trays</label>
            <input type="number" value={editEntry?.[`${prefix}Trays`] ?? 0}
              onChange={e => setEE(`${prefix}Trays`, parseInt(e.target.value) || 0)}
              className={`i-${prefix}`} style={{ borderColor }} min="0" onWheel={pw} />
          </div>
          <div className="r-ff">
            <label>Total No of Vehicles</label>
            <input type="number" value={editEntry?.[`${prefix}Vehicles`] ?? 0}
              onChange={e => setEE(`${prefix}Vehicles`, parseInt(e.target.value) || 0)}
              className={`i-${prefix}`} style={{ borderColor }} min="0" onWheel={pw} />
          </div>
          <div>
            <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: '5px' }}>Shift Hours</div>
            <input
              type="number"
              value={editEntry?.[`${prefix}Hrs`] ?? 8}
              onChange={e => {
                const val = e.target.value;
                if (val === '' || /^\d*\.?\d*$/.test(val)) setEE(`${prefix}Hrs`, val === '' ? '' : parseFloat(val) || val);
              }}
              onBlur={e => {
                const v = parseFloat(e.target.value);
                setEE(`${prefix}Hrs`, isNaN(v) || v <= 0 ? 8 : v);
              }}
              min="0.5" max="24" step="0.5"
              placeholder="e.g. 8, 7.5"
              style={{ width:'100%', padding:'9px 11px', borderRadius:'7px', border:'1.5px solid #ce93d8', fontFamily:"'DM Mono',monospace", fontSize:'13px', background:'var(--tray-bg)', color:'var(--tray-dk)', fontWeight:600, outline:'none', boxSizing:'border-box' }}
              onWheel={pw}
            />
          </div>
          <div className="r-ff">
            <label>Trays / Hour (Auto)</label>
            <input type="number" value={trHr} readOnly className="ro-box" />
          </div>
        </div>
        <div className="r-fg2" style={{ marginTop: '8px' }}>
          <div className="r-ff">
            <label>Total Qty (KG)</label>
            <input type="number" value={editEntry?.[`${prefix}Qty`] ?? 0}
              onChange={e => setEE(`${prefix}Qty`, parseFloat(e.target.value) || 0)}
              className={`i-${prefix}`} style={{ borderColor }} step="0.01" min="0" onWheel={pw} />
          </div>
          <div className="r-ff">
            <label>Qty / Tray (KG) (Auto)</label>
            <input type="number" value={qTray} readOnly className="ro" />
          </div>
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── HEADER ── */}
      <header className="r-header">
        <div className="r-header-left">
          <button className="r-back-btn" onClick={() => navigate('/ppc')}>← Back</button>
          <h1 className="r-title">ROMEO DASHBOARD</h1>
          <span className="r-role-badge">{isAdmin ? '👑 Admin' : '👤 User'}</span>
        </div>
        <div className="r-header-right">
          <button className="r-hdr-btn" onClick={() => navigate('/ppc/contractors')}>👷 Contractors</button>
          {isAdmin && <button className="r-hdr-btn" onClick={() => { if (window.confirm(`Reset all entries for ${selectedDate}?`)) { axios.delete(`${PPC_API_URL}/romeo.php?action=reset&date=${selectedDate}`, auth()).then(() => { showToast('Day reset!'); fetchSummary(); }); } }}>🔄 Reset Day</button>}
          <input type="date" className="r-date-picker" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} />
        </div>
      </header>

      {/* ── TAB NAVIGATION ── */}
      <nav className="r-tab-nav">
        <button className={`r-tab-item${activeTab === 'dashboard' ? ' active' : ''}`} onClick={() => setActiveTab('dashboard')}>
          <span className="r-tab-item-dot" />
          📅 Romeo Dashboard
        </button>
        <button className={`r-tab-item${activeTab === 'reports' ? ' active' : ''}`} onClick={() => setActiveTab('reports')}>
          <span className="r-tab-item-dot" />
          📊 Reports
        </button>
      </nav>

      {/* ── REPORTS TAB ── */}
      {activeTab === 'reports' && <RomeoReportDashboard embedded />}

      {/* ── DASHBOARD TAB ── */}
      {activeTab === 'dashboard' && (
      <main className="r-main">

        {/* Period tabs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div className="r-period-tabs">
            {['daily','weekly','monthly'].map(p => (
              <button key={p} className={`r-period-tab${period === p ? ' active' : ''}`} onClick={() => setPeriod(p)}>
                {p === 'daily' ? '📅 Daily' : p === 'weekly' ? '📆 Weekly' : '🗓️ Monthly'}
              </button>
            ))}
          </div>
          {period === 'weekly' && (
            <div className="r-date-range">
              <label>Week:</label>
              <input type="date" value={weekFrom} onChange={e => setWeekFrom(e.target.value)} />
              <span style={{ color: 'var(--muted)', fontSize: '12px' }}>to</span>
              <input type="date" value={weekTo} onChange={e => setWeekTo(e.target.value)} />
            </div>
          )}
          {period === 'monthly' && (
            <div className="r-date-range">
              <label>Month:</label>
              <input type="month" value={monthPicker} onChange={e => setMonthPicker(e.target.value)}
                style={{ padding: '7px 10px', border: '1px solid var(--border)', borderRadius: '7px', fontFamily: 'inherit', fontSize: '12px', background: '#fff', color: 'var(--text)', cursor: 'pointer', outline: 'none' }} />
              {monthPicker && (
                <button onClick={() => {
                  const parts = monthPicker.split('-');
                  const yr = parseInt(parts[0]); const mo = parseInt(parts[1]);
                  const from = `${yr}-${String(mo).padStart(2,'0')}-01`;
                  const lastDay = new Date(yr, mo, 0).getDate();
                  const to = `${yr}-${String(mo).padStart(2,'0')}-${String(lastDay).padStart(2,'0')}`;
                  fetchReport(from, to);
                }} style={{ padding:'7px 14px',border:'none',borderRadius:'7px',background:'linear-gradient(135deg,#d32f2f,#1565c0)',color:'#fff',fontFamily:'inherit',fontSize:'12px',fontWeight:600,cursor:'pointer' }}>
                  Generate
                </button>
              )}
            </div>
          )}
        </div>

        {/* ══ DAILY ══ */}
        {period === 'daily' && (
          <>
            {/* Both line stat rows always visible */}
            {LINES.map(line => renderLineStats(line))}

            {/* Entry table card */}
            <div className="r-card">
              <div className="r-card-header">
                <span className="r-card-title">Entries — {selectedDate}</span>
                <div className="r-card-actions">
                  {/* Line tabs inside card header */}
                  <div className="r-line-tab-bar">
                    {LINES.map(line => (
                      <button key={line} className={`r-line-tab${activeLine === line ? ' active' : ''}`}
                        onClick={() => setActiveLine(line)}>
                        <span className="r-line-dot" />
                        {line}
                      </button>
                    ))}
                  </div>
                  <button className="r-btn-add-global" onClick={openAddGlobal}>＋ Add Entry</button>
                  <button className="r-export-btn" onClick={handleExport}>📥 Export</button>
                </div>
              </div>
              {renderTable(activeLine)}
            </div>
          </>
        )}

        {/* ══ WEEKLY ══ */}
        {period === 'weekly' && (
          <>
            {/* FIX B1: stat cards now read from reportData instead of hardcoded — */}
            <div className="r-stats-grid">
              <div className="r-stat-card">
                <span className="r-stat-icon">📅</span>
                <div>
                  <div className="r-stat-label">Period</div>
                  <div className="r-stat-value" style={{ fontSize:'11px', color:'var(--blue)' }}>
                    {weekFrom && weekTo ? `${weekFrom} – ${weekTo}` : 'Select week'}
                  </div>
                </div>
              </div>
              <div className="r-stat-card tray">
                <span className="r-stat-icon">🗂️</span>
                <div>
                  <div className="r-stat-label">Total Trays</div>
                  <div className="r-stat-value tray">
                    {reportLoading ? '…' : reportData ? (reportData.totalTrays || 0).toLocaleString() : '—'}
                  </div>
                </div>
              </div>
              <div className="r-stat-card green">
                <span className="r-stat-icon">⚖️</span>
                <div>
                  <div className="r-stat-label">Total Qty</div>
                  <div className="r-stat-value green">
                    {reportLoading ? '…' : reportData ? `${fmt(reportData.totalQuantity)} KG` : '— KG'}
                  </div>
                </div>
              </div>
              <div className="r-stat-card">
                <span className="r-stat-icon">👷</span>
                <div>
                  <div className="r-stat-label">Associates</div>
                  <div className="r-stat-value blue">
                    {reportLoading ? '…' : reportData ? (reportData.totalAssociates || 0).toLocaleString() : '—'}
                  </div>
                </div>
              </div>
              <div className="r-stat-card amber">
                <span className="r-stat-icon">📈</span>
                <div>
                  <div className="r-stat-label">Avg Trays/Day</div>
                  <div className="r-stat-value amber">
                    {reportLoading ? '…' : reportData ? fmt(reportData.avgTraysPerDay || 0) : '—'}
                  </div>
                </div>
              </div>
            </div>

            <div className="r-breakdown-card">
              <div className="r-breakdown-hdr">
                <span className="r-breakdown-title">
                  Weekly Breakdown {weekFrom && weekTo ? `— ${weekFrom} to ${weekTo}` : ''}
                </span>
                <button className="r-export-btn" onClick={handleExport}>📥 Export</button>
              </div>
              {!weekFrom || !weekTo ? (
                <div style={{ padding:'32px', textAlign:'center', color:'var(--muted)', fontSize:'13px' }}>
                  Select a week range above to load data.
                </div>
              ) : reportLoading ? (
                <div style={{ padding:'32px', textAlign:'center', color:'var(--muted)', fontSize:'13px' }}>Loading…</div>
              ) : !reportData?.detailedData?.length ? (
                <div style={{ padding:'32px', textAlign:'center', color:'var(--muted)', fontSize:'13px' }}>
                  No data available for selected range.
                </div>
              ) : (
                <div className="r-table-wrap">
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px', minWidth:'700px' }}>
                    <thead><tr>
                      <th style={{ padding:'8px 12px', background:'#f8f9fa', borderBottom:'1px solid #e2e6ea', fontWeight:600, fontSize:'11px', color:'#6b7280', textAlign:'left' }}>Date</th>
                      <th style={{ padding:'8px 12px', background:'#f8f9fa', borderBottom:'1px solid #e2e6ea', fontWeight:600, fontSize:'11px', color:'#7b1fa2' }}>Line</th>
                      <th style={{ padding:'8px 12px', background:'#f8f9fa', borderBottom:'1px solid #e2e6ea', fontWeight:600, fontSize:'11px', color:'#1a1d23' }}>Contractor</th>
                      <th style={{ padding:'8px 12px', background:'#f8f9fa', borderBottom:'1px solid #e2e6ea', fontWeight:600, fontSize:'11px', color:'#6b7280' }}>Shift</th>
                      <th style={{ padding:'8px 12px', background:'#f8f9fa', borderBottom:'1px solid #e2e6ea', fontWeight:600, fontSize:'11px', color:'#1565c0' }}>Male Assoc</th>
                      <th style={{ padding:'8px 12px', background:'#f8f9fa', borderBottom:'1px solid #e2e6ea', fontWeight:600, fontSize:'11px', color:'#d97706' }}>Supp Assoc</th>
                      <th style={{ padding:'8px 12px', background:'#f3e5f5', borderBottom:'1px solid #e2e6ea', fontWeight:600, fontSize:'11px', color:'#7b1fa2' }}>Trays</th>
                      <th style={{ padding:'8px 12px', background:'#f3e5f5', borderBottom:'1px solid #e2e6ea', fontWeight:600, fontSize:'11px', color:'#7b1fa2' }}>Vehicles</th>
                      <th style={{ padding:'8px 12px', background:'#dbeafe', borderBottom:'1px solid #e2e6ea', fontWeight:600, fontSize:'11px', color:'#1565c0' }}>Qty (KG)</th>
                      <th style={{ padding:'8px 12px', background:'#dbeafe', borderBottom:'1px solid #e2e6ea', fontWeight:600, fontSize:'11px', color:'#1565c0' }}>Qty/Tray</th>
                    </tr></thead>
                    <tbody>
                      {reportData.detailedData.map((r, i) => (
                        <tr key={i} style={{ borderBottom:'1px solid #e5e7eb' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f5f7ff'}
                          onMouseLeave={e => e.currentTarget.style.background = ''}>
                          <td style={{ padding:'8px 12px', fontFamily:"'DM Mono',monospace", fontSize:'11px', color:'#6b7280' }}>{fmtDate(r.entry_date)}</td>
                          <td style={{ padding:'8px 12px', fontWeight:700, color:'#7b1fa2', fontSize:'11px', textAlign:'center' }}>{r.line}</td>
                          <td style={{ padding:'8px 12px', fontWeight:600 }}>{r.contractor}</td>
                          <td style={{ padding:'8px 12px', textAlign:'center' }}>
                            <span style={{ padding:'2px 8px', borderRadius:'10px', fontSize:'10px', fontWeight:700, background: r.shift==='DAY'?'rgba(211,47,47,.1)':'rgba(21,101,192,.1)', color: r.shift==='DAY'?'#d32f2f':'#1565c0' }}>
                              {r.shift === 'DAY' ? '☀️ Day' : '🌙 Night'}
                            </span>
                          </td>
                          <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:"'DM Mono',monospace", color:'#1565c0' }}>{r.male_associates || 0}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:"'DM Mono',monospace", color:'#d97706' }}>{r.supporting_male || 0}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:"'DM Mono',monospace", fontWeight:700, color:'#7b1fa2' }}>{r.total_no_trays || 0}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:"'DM Mono',monospace" }}>{r.total_no_vehicles || 0}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:"'DM Mono',monospace", fontWeight:700, color:'#1565c0' }}>{fmt(r.total_quantity)}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:"'DM Mono',monospace", color:'#059669' }}>{fmt(r.qty_per_tray)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:'#f0f2f5', borderTop:'2px solid #e2e6ea', fontWeight:700 }}>
                        <td colSpan={4} style={{ padding:'8px 12px', textAlign:'left' }}>TOTAL ({reportData.detailedData.length} entries)</td>
                        <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:"'DM Mono',monospace", color:'#1565c0' }}>{reportData.detailedData.reduce((s,r) => s + parseInt(r.male_associates || 0), 0)}</td>
                        <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:"'DM Mono',monospace", color:'#d97706' }}>{reportData.detailedData.reduce((s,r) => s + parseInt(r.supporting_male || 0), 0)}</td>
                        <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:"'DM Mono',monospace", color:'#7b1fa2' }}>{reportData.totalTrays}</td>
                        <td></td>
                        <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:"'DM Mono',monospace", color:'#1565c0' }}>{fmt(reportData.totalQuantity)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* ══ MONTHLY ══ */}
        {period === 'monthly' && (
          <>
            {/* FIX B2: stat cards now read from reportData instead of hardcoded — */}
            <div className="r-stats-grid">
              <div className="r-stat-card">
                <span className="r-stat-icon">🗓️</span>
                <div>
                  <div className="r-stat-label">Period</div>
                  <div className="r-stat-value" style={{ fontSize:'11px', color:'var(--blue)' }}>
                    {monthPicker || 'Select month'}
                  </div>
                </div>
              </div>
              <div className="r-stat-card tray">
                <span className="r-stat-icon">🗂️</span>
                <div>
                  <div className="r-stat-label">Total Trays</div>
                  <div className="r-stat-value tray">
                    {reportLoading ? '…' : reportData ? (reportData.totalTrays || 0).toLocaleString() : '—'}
                  </div>
                </div>
              </div>
              <div className="r-stat-card green">
                <span className="r-stat-icon">⚖️</span>
                <div>
                  <div className="r-stat-label">Total Qty</div>
                  <div className="r-stat-value green">
                    {reportLoading ? '…' : reportData ? `${fmt(reportData.totalQuantity)} KG` : '— KG'}
                  </div>
                </div>
              </div>
              <div className="r-stat-card amber">
                <span className="r-stat-icon">📈</span>
                <div>
                  <div className="r-stat-label">Avg Trays/Day</div>
                  <div className="r-stat-value amber">
                    {reportLoading ? '…' : reportData ? fmt(reportData.avgTraysPerDay || 0) : '—'}
                  </div>
                </div>
              </div>
              <div className="r-stat-card">
                <span className="r-stat-icon">📊</span>
                <div>
                  <div className="r-stat-label">Working Days</div>
                  <div className="r-stat-value blue">
                    {reportLoading ? '…' : reportData ? (reportData.workingDays || 0) : '—'}
                  </div>
                </div>
              </div>
            </div>

            <div className="r-breakdown-card">
              <div className="r-breakdown-hdr">
                <span className="r-breakdown-title">Monthly Breakdown {monthPicker ? `— ${monthPicker}` : ''}</span>
                <button className="r-export-btn" onClick={handleExport}>📥 Export</button>
              </div>
              {!monthPicker ? (
                <div style={{ padding:'32px', textAlign:'center', color:'var(--muted)', fontSize:'13px' }}>
                  Select a month above to load data.
                </div>
              ) : reportLoading ? (
                <div style={{ padding:'32px', textAlign:'center', color:'var(--muted)', fontSize:'13px' }}>Loading…</div>
              ) : !reportData?.daySummary?.length ? (
                <div style={{ padding:'32px', textAlign:'center', color:'var(--muted)', fontSize:'13px' }}>
                  No data available for selected month.
                </div>
              ) : (
                <div className="r-table-wrap">
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px', minWidth:'600px' }}>
                    <thead><tr>
                      <th style={{ padding:'8px 12px', background:'#f8f9fa', borderBottom:'1px solid #e2e6ea', fontWeight:600, fontSize:'11px', color:'#6b7280', textAlign:'left' }}>Date</th>
                      <th style={{ padding:'8px 12px', background:'#f3e5f5', borderBottom:'1px solid #e2e6ea', fontWeight:600, fontSize:'11px', color:'#7b1fa2', textAlign:'right' }}>Total Trays</th>
                      <th style={{ padding:'8px 12px', background:'#f8f9fa', borderBottom:'1px solid #e2e6ea', fontWeight:600, fontSize:'11px', color:'#1565c0', textAlign:'right' }}>Associates</th>
                      <th style={{ padding:'8px 12px', background:'#dbeafe', borderBottom:'1px solid #e2e6ea', fontWeight:600, fontSize:'11px', color:'#1565c0', textAlign:'right' }}>Total Qty (KG)</th>
                      <th style={{ padding:'8px 12px', background:'#f8f9fa', borderBottom:'1px solid #e2e6ea', fontWeight:600, fontSize:'11px', color:'#059669', textAlign:'right' }}>Avg KG/Tray</th>
                    </tr></thead>
                    <tbody>
                      {reportData.daySummary.map((r, i) => (
                        <tr key={i} style={{ borderBottom:'1px solid #e5e7eb' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f5f7ff'}
                          onMouseLeave={e => e.currentTarget.style.background = ''}>
                          <td style={{ padding:'8px 12px', fontFamily:"'DM Mono',monospace", fontSize:'11px', color:'#6b7280' }}>{fmtDate(r.entry_date)}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:"'DM Mono',monospace", fontWeight:700, color:'#7b1fa2' }}>{r.total_trays || 0}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:"'DM Mono',monospace", color:'#1565c0' }}>{r.total_associates || 0}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:"'DM Mono',monospace", fontWeight:700, color:'#1565c0' }}>{fmt(r.total_quantity)}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:"'DM Mono',monospace", color:'#059669' }}>{fmt(r.avg_kg_per_tray)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:'#f0f2f5', borderTop:'2px solid #e2e6ea', fontWeight:700 }}>
                        <td style={{ padding:'8px 12px' }}>TOTAL ({reportData.daySummary.length} days)</td>
                        <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:"'DM Mono',monospace", color:'#7b1fa2' }}>{reportData.totalTrays}</td>
                        <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:"'DM Mono',monospace", color:'#1565c0' }}>{reportData.totalAssociates}</td>
                        <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:"'DM Mono',monospace", color:'#1565c0' }}>{fmt(reportData.totalQuantity)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </main>
      )} {/* end activeTab === 'dashboard' */}

      {/* ════ ADD ENTRY MODAL ════ */}
      {showAdd && (
        <div className="r-overlay" onClick={() => setShowAdd(false)}>
          <div className="r-modal" style={{ maxWidth: '760px' }} onClick={e => e.stopPropagation()}>
            <div className="r-modal-hdr">
              <div>
                <div className="r-modal-title">➕ Add Entry — Romeo</div>
                <div className="r-modal-sub">{addForm.line}{addForm.contractor ? ` · ${addForm.contractor}` : ''} · {addForm.date} · {addForm.shift} shift</div>
              </div>
              <button className="r-modal-close" onClick={() => setShowAdd(false)}>×</button>
            </div>
            <div className="r-modal-scroll">

              {/* Line selector */}
              <div className="r-f-sec info-s">
                <div className="r-f-lbl info">Select Line</div>
                <div className="r-line-sel">
                  {LINES.map(line => (
                    <button key={line} className={`r-line-sel-btn${addForm.line === line ? ' active' : ''}`}
                      onClick={() => setAF('line', line)}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: line === 'ROMEO 1' ? '#7b1fa2' : '#1565c0', display: 'inline-block' }} />
                      {line}
                    </button>
                  ))}
                </div>
              </div>

              {/* Contractor */}
              <div className="r-ff">
                <label>Contractor</label>
                <select value={addForm.contractor} onChange={e => setAF('contractor', e.target.value)}
                  style={{ width:'100%',padding:'9px 11px',borderRadius:'7px',border:'1.5px solid var(--border)',fontFamily:'DM Sans,sans-serif',fontSize:'13px',background:'#fff',outline:'none',boxSizing:'border-box' }}>
                  <option value="">— Select Contractor —</option>
                  {contractors.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
              </div>

              {/* Date + Shift */}
              <div className="r-fg2">
                <div className="r-ff">
                  <label>Date</label>
                  <input type="date" value={addForm.date} onChange={e => setAF('date', e.target.value)} />
                </div>
                <div>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: '5px' }}>Shift</div>
                  <div className="r-shift-btns">
                    {['DAY','NIGHT'].map(s => (
                      <button key={s} className={`r-shift-btn${addForm.shift === s ? ' active' : ''}`}
                        onClick={() => setAF('shift', s)}>
                        {s === 'DAY' ? '☀️' : '🌙'} {s} {addForm.shift === s && '✓'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

                {/* Associates — Gender Toggle */}
              <div className="r-f-sec day-s">
                <div className="r-f-lbl day">👷 Associates</div>

                {/* Gender selector */}
                <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: '6px' }}>Gender Type</div>
                <div className="r-gender-btns" style={{ marginBottom: '12px' }}>
                  {[
                    { val: 'MALE',   icon: '👨', label: 'Supporting Male' },
                    { val: 'FEMALE', icon: '👩', label: 'Supporting Female' },
                  ].map(g => {
                    const isActive = addForm.gender === g.val;
                    const activeClass = g.val === 'MALE' ? 'male-active' : 'female-active';
                    return (
                      <button key={g.val}
                        className={`r-gender-btn${isActive ? ` ${activeClass}` : ''}`}
                        onClick={() => setAF('gender', g.val)}>
                        {g.icon} {g.label} {isActive && '✓'}
                      </button>
                    );
                  })}
                </div>

                {/* Count inputs */}
                <div className="r-gender-count">
                  {addForm.gender === 'MALE' && (
                    <div className="r-gender-count-box">
                      <label>👨 Supporting Male Count</label>
                      <input type="number" value={addForm.assocMale}
                        onChange={e => setAF('assocMale', parseInt(e.target.value) || 0)}
                        className="male-inp" placeholder="0" min="0" onWheel={pw} />
                    </div>
                  )}
                  {addForm.gender === 'FEMALE' && (
                    <div className="r-gender-count-box">
                      <label>👩 Supporting Female Count</label>
                      <input type="number" value={addForm.suppMale}
                        onChange={e => setAF('suppMale', parseInt(e.target.value) || 0)}
                        className="female-inp" placeholder="0" min="0" onWheel={pw} />
                    </div>
                  )}
                  <div className="r-gender-count-box">
                    <label>Total Associates (Auto)</label>
                    <input type="number"
                      value={(parseInt(addForm.assocMale) || 0) + (parseInt(addForm.suppMale) || 0)}
                      readOnly className="total-inp" />
                  </div>
                </div>
              </div>

              {/* Trays + Vehicles */}
              <div className="r-f-sec tray-s">
                <div className="r-f-lbl tray">🗂️ Trays &amp; Vehicles</div>
                <div className="r-fg2">
                  <div className="r-ff"><label>Total No of Trays</label><input type="number" value={addForm.trays} onChange={e => setAF('trays', e.target.value)} className="i-tray" placeholder="0" min="0" onWheel={pw} /></div>
                  <div className="r-ff"><label>Total No of Vehicles</label><input type="number" value={addForm.vehicles} onChange={e => setAF('vehicles', e.target.value)} className="i-tray" placeholder="0" min="0" onWheel={pw} /></div>
                </div>
                <div className="r-fg2" style={{ marginTop: '10px' }}>
                  <div>
                    <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: '5px' }}>Shift Hours</div>
                    <input
                      type="number"
                      value={addForm.shiftHours}
                      onChange={e => {
                        const val = e.target.value;
                        if (val === '' || /^\d*\.?\d*$/.test(val)) setAF('shiftHours', val === '' ? '' : parseFloat(val) || val);
                      }}
                      onBlur={e => {
                        const v = parseFloat(e.target.value);
                        setAF('shiftHours', isNaN(v) || v <= 0 ? 8 : v);
                      }}
                      min="0.5" max="24" step="0.5"
                      placeholder="e.g. 8, 7.5"
                      style={{ width:'100%', padding:'9px 11px', borderRadius:'7px', border:'1.5px solid #ce93d8', fontFamily:"'DM Mono',monospace", fontSize:'13px', background:'var(--tray-bg)', color:'var(--tray-dk)', fontWeight:600, outline:'none', boxSizing:'border-box' }}
                      onWheel={pw}
                    />
                  </div>
                  <div>
                    <div className="r-ff" style={{ marginBottom: '8px' }}>
                      <label>Trays / Hour (Auto)</label>
                      <input type="number" value={traysPerHr} readOnly className="ro-box" />
                    </div>
                    <div className="r-ff">
                      <label>Avg KG / Tray</label>
                      <input type="number" value={addForm.kgPerTray} onChange={e => setAF('kgPerTray', parseFloat(e.target.value) || 21)}
                        step="0.5" min="20" max="22" style={{ width: '100%', padding: '9px 11px', borderRadius: '7px', border: '1.5px solid #ce93d8', fontFamily: "'DM Mono',monospace", fontSize: '12px', background: '#fff', outline: 'none', boxSizing: 'border-box' }} onWheel={pw} />
                      <div style={{ fontSize: '9px', color: 'var(--tray-dk)', marginTop: '3px', fontWeight: 600 }}>⚡ Per tray: 20–22 KG</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quantity — Lot-wise Table */}
              <div className="r-f-sec qty-s">
                <div className="r-f-lbl qty">⚖️ Quantity — Lot-wise Entry</div>

                {/* Add More button */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
                  <button
                    className="r-qty-add-btn"
                    onClick={() => setAF('quantityRows', [...addForm.quantityRows, emptyQtyRow()])}
                  >
                    ➕ Add More
                  </button>
                </div>

                {/* Table */}
                <div style={{ overflowX: 'auto' }}>
                  <table className="r-qty-tbl">
                    <thead>
                      <tr>
                        <th style={{ width: '34%', textAlign: 'left', paddingLeft: '10px' }}>Lot Number</th>
                        <th style={{ width: '18%' }}>H.ON Count</th>
                        <th style={{ width: '22%' }}>H.ON Qty (KG)</th>
                        <th style={{ width: '16%' }}>Hours</th>
                        <th style={{ width: '10%' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {addForm.quantityRows.map((row, idx) => (
                        <tr key={row.id}>
                          <td>
                            <input
                              type="text"
                              value={row.lotNumber}
                              onChange={e => updateQtyRow(idx, 'lotNumber', e.target.value)}
                              placeholder="e.g. BAPCCC4LOT-2526-0001"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={row.honCount}
                              onChange={e => updateQtyRow(idx, 'honCount', e.target.value)}
                              placeholder="0"
                              min="0"
                              onWheel={pw}
                              style={{ textAlign: 'center' }}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={row.honQty}
                              onChange={e => updateQtyRow(idx, 'honQty', e.target.value)}
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                              onWheel={pw}
                              style={{ textAlign: 'right' }}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={row.hours}
                              onChange={e => updateQtyRow(idx, 'hours', e.target.value)}
                              placeholder="0"
                              step="0.5"
                              min="0"
                              onWheel={pw}
                              style={{ textAlign: 'center' }}
                            />
                          </td>
                          <td style={{ textAlign: 'center', background: 'transparent' }}>
                            {addForm.quantityRows.length > 1 && (
                              <button
                                className="r-qty-del-btn"
                                onClick={() => setAF('quantityRows', addForm.quantityRows.filter((_, i) => i !== idx))}
                                title="Remove row"
                              >×</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Total row */}
                <div className="r-qty-total">
                  <span style={{ fontSize: '10px', color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.4px' }}>Total H.ON Qty:</span>
                  <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '15px', fontWeight: 700 }}>
                    {fmt((addForm.quantityRows || []).reduce((s, r) => s + (parseFloat(r.honQty) || 0), 0))} KG
                  </span>
                </div>
              </div>

              {/* Remarks */}
              <div className="r-ff"><label>Remarks</label><input type="text" value={addForm.remarks} onChange={e => setAF('remarks', e.target.value)} placeholder="Optional notes..." /></div>

            </div>
            <div className="r-modal-foot">
              <button className="r-btn-cancel" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="r-btn-save" onClick={handleSaveAdd} disabled={submitting}>{submitting ? 'Saving...' : 'Save Entry'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ════ EDIT ENTRY MODAL ════ */}
      {showEdit && editEntry && (
        <div className="r-overlay" onClick={() => setShowEdit(false)}>
          <div className="r-modal" style={{ maxWidth: '720px' }} onClick={e => e.stopPropagation()}>
            <div className="r-modal-hdr">
              <div>
                <div className="r-modal-title">✏️ Edit Entry</div>
                <div className="r-modal-sub">{editEntry.contractor} · {editEntry.date}</div>
              </div>
              <button className="r-modal-close" onClick={() => setShowEdit(false)}>×</button>
            </div>
            <div className="r-modal-scroll">

              {/* Contractor + Line + Date */}
              <div className="r-fg4">
                <div className="r-ff">
                  <label>Contractor</label>
                  <select value={editEntry.contractor} onChange={e => setEE('contractor', e.target.value)}
                    style={{ width:'100%',padding:'9px 11px',borderRadius:'7px',border:'1.5px solid var(--border)',fontFamily:'DM Sans,sans-serif',fontSize:'13px',background:'#fff',outline:'none',boxSizing:'border-box' }}>
                    {contractors.map(name => <option key={name} value={name}>{name}</option>)}
                  </select>
                </div>
                <div className="r-ff">
                  <label>Line</label>
                  <select value={editEntry.line || 'ROMEO 1'} onChange={e => setEE('line', e.target.value)}
                    style={{ width:'100%',padding:'9px 11px',borderRadius:'7px',border:'1.5px solid #ce93d8',fontFamily:'DM Sans,sans-serif',fontSize:'13px',background:'#f3e5f5',outline:'none',boxSizing:'border-box',color:'#7b1fa2',fontWeight:600 }}>
                    {LINES.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div className="r-ff">
                  <label>Date</label>
                  <input type="date" value={editEntry.date} onChange={e => setEE('date', e.target.value)} />
                </div>
                <div className="r-ff">
                  <label>Shifts with Data</label>
                  <div style={{ display:'flex', gap:'6px', paddingTop:'4px', flexWrap:'wrap' }}>
                    {editEntry.dayEntryId   && <span style={{ padding:'4px 10px',borderRadius:'12px',background:'rgba(211,47,47,.1)',color:'var(--red)',fontSize:'11px',fontWeight:600 }}>☀️ Day</span>}
                    {editEntry.nightEntryId && <span style={{ padding:'4px 10px',borderRadius:'12px',background:'rgba(21,101,192,.1)',color:'var(--blue)',fontSize:'11px',fontWeight:600 }}>🌙 Night</span>}
                  </div>
                </div>
              </div>

              {/* Associates */}
              <div className="r-f-sec neut-s">
                <div className="r-f-lbl">👷 Associates &amp; Support (Male only)</div>
                <div className="r-fg2">
                  <div className="r-ff"><label>Male Associates</label><input type="number" value={editEntry.assocMale} onChange={e => setEE('assocMale', parseInt(e.target.value)||0)} min="0" onWheel={pw} /></div>
                  <div className="r-ff"><label>Support Male</label><input type="number" value={editEntry.suppMale} onChange={e => setEE('suppMale', parseInt(e.target.value)||0)} min="0" onWheel={pw} /></div>
                </div>
              </div>

              {/* Day Shift */}
              {editEntry.dayEntryId && (
                <EditShiftSection label="☀️ Day Shift" prefix="day" cls="day-s" borderColor="#fca5a5" />
              )}

              {/* Night Shift */}
              {editEntry.nightEntryId && (
                <EditShiftSection label="🌙 Night Shift" prefix="night" cls="night-s" borderColor="#93c5fd" />
              )}

              {/* Reason for non-admin */}
              {!isAdmin && !canEdit && (
                <div className="r-ff">
                  <label>Reason for Edit</label>
                  <textarea value={editReason} onChange={e => setEditReason(e.target.value)}
                    placeholder="Enter reason" rows={2}
                    style={{ width: '100%', padding: '9px 11px', borderRadius: '7px', border: '1.5px solid var(--border)', fontFamily: 'inherit', fontSize: '12px', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
                </div>
              )}

            </div>
            <div className="r-modal-foot">
              <button className="r-btn-cancel" onClick={() => setShowEdit(false)}>Cancel</button>
              <button className="r-btn-save" onClick={handleSaveEdit}>{isAdmin || canEdit ? 'Save Changes' : 'Submit Request'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ════ DELETE MODAL ════ */}
      {showDel && delRow && (
        <div className="r-overlay" onClick={() => setShowDel(false)}>
          <div className="r-modal" style={{ maxWidth: '360px' }} onClick={e => e.stopPropagation()}>
            <div className="r-del-body">
              <div className="r-del-icon">🗑️</div>
              <div className="r-del-ttl">Delete Entry?</div>
              <div className="r-del-sub">This will delete all shift entries for</div>
              <div className="r-del-nm">{delRow.contractor} — {fmtDate(selectedDate)}</div>
              <div className="r-del-btns">
                <button className="r-btn-cancel" onClick={() => setShowDel(false)}>Cancel</button>
                <button className="r-btn-del-ok" onClick={handleDel}>Yes, Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast && <div className={`r-toast ${toast.type}`}>{toast.msg}</div>}

      <style>{`@keyframes rSlideUp{from{transform:translateY(22px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
    </>
  );
};

export default RomeoDashboard;
